import { errorMessageToastContent } from '@festina-life/components'
import { typeUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { Alert, toast } from '@festina-life/ui/feedback'
import { FormikForm, FormikTextArea } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack } from '@festina-life/ui/structure'
import * as Yup from 'yup'

import { isProtectedBranchCategory } from '../../../../uiModels/branches/types'
import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import { useInvalidateBranchAfterHistoryRewrite } from '../../../../uiModels/branches/useInvalidateBranchAfterHistoryRewrite'
import { usePushWorkspaceToBaseBranch } from '../../../../uiModels/workspace/usePushWorkspaceToBaseBranch'
import { useWorkspaceStatus } from '../../../../uiModels/workspace/useWorkspaceStatus'
import { useRequiredWorkspaceOperationsContext } from '../../../../workspace/operationsContext'
import { useWorkspaceManagerContext } from '../../../../workspace/workspaceManagerContext'
import { useChangesetApplicationErrors } from '../workspaceOperations/useChangesetApplicationErrors'

type PushToChangesetInput = {
  changesetDescription: string
}

const validate = typeUtils.createValidator<PushToChangesetInput>()

type Props = {
  buttonText: string
}

export const PushWorkspaceToBranchForm = ({ buttonText }: Props) => {
  const translate = useTranslate()
  const { workspaceKey, workspaceValidationErrors } =
    useRequiredWorkspaceOperationsContext()
  const { mutateAsync: pushWorkspace, isPending: isPushing } =
    usePushWorkspaceToBaseBranch(workspaceKey)
  const changesetApplicationErrors = useChangesetApplicationErrors()
  const { revalidateWorkspace, onDelete } = useWorkspaceManagerContext()
  const invalidateBranch = useInvalidateBranchAfterHistoryRewrite()
  const status = useWorkspaceStatus(workspaceKey)
  const conciseBranch = useConciseBranch({
    server: workspaceKey.branchKey.server,
    branchName: workspaceKey.branchKey.name,
    shouldFallbackToStandard: false,
  })

  const validationSchema = Yup.object({
    changesetDescription: Yup.string().required(
      translate('validation.required'),
    ),
  })

  const handlePushToWorkspace = async (values: PushToChangesetInput) => {
    try {
      await pushWorkspace({ description: values.changesetDescription })
      await onDelete(workspaceKey)

      void revalidateWorkspace(workspaceKey)
      void invalidateBranch(workspaceKey.branchKey)

      toast({
        title: translate('branchesTree.toast.pushedWorkspace', {
          workspaceName: workspaceKey.name,
          baseBranchName: workspaceKey.branchKey.name,
        }),
        variant: 'success',
      })
    } catch (error) {
      toast(errorMessageToastContent({ error, translate }))
    }
  }

  const hasValidationErrors = !!Array.from(
    workspaceValidationErrors.values(),
  ).some((error) => !!error)
  const isBaseBranchProtected = isProtectedBranchCategory(
    conciseBranch?.branchCategory,
  )
  const hasChangesetApplicationErrors = !!changesetApplicationErrors.length
  const isWorkspaceBranchAvailable = !!conciseBranch
  const isOutOfSync = status === 'outOfSync'

  const isPushButtonDisabled =
    hasValidationErrors ||
    isBaseBranchProtected ||
    hasChangesetApplicationErrors ||
    !isWorkspaceBranchAvailable ||
    isOutOfSync

  return (
    <Stack gap="condensed" padding="none" className="w-full py-1 flex-col">
      {hasValidationErrors && (
        <Alert
          severity="warning"
          title={translate(
            'workspaceManagement.workspacePushForm.error.validationError',
          )}
        />
      )}
      {isBaseBranchProtected && (
        <Alert
          severity="warning"
          title={translate(
            'workspaceManagement.workspacePushForm.error.baseBranchProtected',
          )}
        />
      )}
      {hasChangesetApplicationErrors && (
        <Alert
          severity="warning"
          title={translate(
            'workspaceManagement.workspacePushForm.error.hasChangesetApplicationErrors',
          )}
        />
      )}
      {!isWorkspaceBranchAvailable && (
        <Alert
          severity="warning"
          title={translate(
            'workspaceManagement.workspacePushForm.error.baseBranchUnavailable',
          )}
        />
      )}
      {isOutOfSync && (
        <Alert
          severity="warning"
          title={translate(
            'workspaceManagement.workspacePushForm.error.outOfSync',
          )}
        />
      )}
      <FormikForm
        initialValues={{ changesetDescription: '' }}
        onSubmit={handlePushToWorkspace}
        validationSchema={validationSchema}
        className="w-full"
        validateOnBlur={false}
      >
        <FormikTextArea
          isRequired
          name={validate('changesetDescription')}
          label={translate(
            'workspaceManagement.workspacePushForm.changesetDescription.label',
          )}
          description={translate(
            'workspaceManagement.workspacePushForm.changesetDescription.description',
          )}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            isLoading={isPushing}
            variant="primary"
            disabled={isPushButtonDisabled}
          >
            {buttonText}
          </Button>
        </div>
      </FormikForm>
    </Stack>
  )
}
